import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { canManagePerformance } from '../utils/roleHelpers';
import {
  buildMarkPayload,
  eventsForGroup,
  formAfterGroupChange,
  groupsForDiscipline,
  groupsForTeacher,
  studentsInGroup,
  teacherMustChooseGroup,
} from './performanceForm';
import { getApiErrorMessage } from '../utils/apiError';
import ConfirmDialog from '../components/ConfirmDialog';
import './PerformanceResults.css';

const LIMA_TIME_ZONE = 'America/Lima';

const emptyFilters = () => ({
  disciplineId: '',
  eventId: '',
  studentId: '',
  groupId: '',
  dateFrom: '',
  dateTo: '',
  goal: '',
});

const asList = (data) => (Array.isArray(data) ? data : data?.items || data?.results || []);

const parseTimeToMs = (raw) => {
  const value = (raw || '').trim().replace(',', '.');
  if (!value) return null;
  const match = value.match(/^(?:(\d+):)?(\d+)(?:\.(\d{1,3}))?$/);
  if (!match) return null;
  const minutes = Number(match[1] || 0);
  const seconds = Number(match[2]);
  let fraction = match[3] || '0';
  while (fraction.length < 3) fraction += '0';
  const ms = minutes * 60000 + seconds * 1000 + Number(fraction.slice(0, 3));
  return ms > 0 ? ms : null;
};

const formatMs = (ms) => {
  if (!ms || ms < 1) return '—';
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  const cents = Math.floor((ms % 1000) / 10);
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(cents).padStart(2, '0')}`;
};

/** El backend guarda recorded_at en UTC sin zona; forzamos Z para no confundirlo con hora local. */
const parseApiDateTimeUtc = (raw) => {
  if (!raw) return null;
  const value = String(raw).trim();
  if (!value) return null;
  const normalized = value.includes('T') ? value : value.replace(' ', 'T');
  const hasOffset = /[zZ]$|[+-]\d{2}:\d{2}$/.test(normalized);
  const parsed = new Date(hasOffset ? normalized : `${normalized}Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateParts = (raw) => {
  const parsed = parseApiDateTimeUtc(raw);
  if (!parsed) return { date: raw ? String(raw) : '—', time: '' };
  const date = new Intl.DateTimeFormat('es-PE', {
    timeZone: LIMA_TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(parsed);
  const time = new Intl.DateTimeFormat('es-PE', {
    timeZone: LIMA_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(parsed);
  return { date, time };
};

const formatResult = (mark) => {
  const details = mark.details;
  if (details && Array.isArray(details.attempts)) {
    const goals = Number.isInteger(details.goals)
      ? details.goals
      : details.attempts.filter(Boolean).length;
    return `${goals} / ${details.attempts.length}`;
  }
  if (mark.event_kind === 'score') return '—';
  return formatMs(mark.time_ms);
};

const formatEffectiveness = (mark) => {
  const details = mark.details;
  if (details && Array.isArray(details.attempts) && details.attempts.length > 0) {
    const goals = Number.isInteger(details.goals)
      ? details.goals
      : details.attempts.filter(Boolean).length;
    return `${Math.round((goals / details.attempts.length) * 100)}%`;
  }
  return '—';
};

const splitDistance = (eventName, count) => {
  const name = eventName || '';
  const relay = name.match(/(\d+)\s*[x×]\s*(\d+)/i);
  if (relay) return { kind: 'relay', size: Number(relay[2]) };
  const meters = name.match(/(\d+)\s*m\b/i);
  if (meters && count > 0) {
    const total = Number(meters[1]);
    if (total > 0 && total % count === 0) return { kind: 'distance', size: total / count };
  }
  return null;
};

const splitTramoLabel = (eventName, index, count) => {
  const distance = splitDistance(eventName, count);
  if (distance?.kind === 'relay') return `Relevo ${index + 1}`;
  if (distance?.kind === 'distance') return `${distance.size * (index + 1)} m`;
  return `Tramo ${index + 1}`;
};

const markDetail = (mark) => {
  const details = mark.details || {};
  if (Array.isArray(details.attempts) && details.attempts.length) {
    return {
      mode: 'attempts',
      note: 'Cada número es un tiro. Gol suma a la efectividad y fallo no.',
      rows: details.attempts.map((goal, index) => ({
        key: `shot-${index}`,
        text: `${index + 1}. ${goal ? 'Gol' : 'Fallo'}`,
        kind: goal ? 'goal' : 'miss',
      })),
    };
  }
  if (Array.isArray(details.splits_ms) && details.splits_ms.length) {
    const cumulative = Array.isArray(details.cumulative_ms) ? details.cumulative_ms : [];
    const times = details.splits_ms.map((item) => Number(item)).filter((ms) => Number.isFinite(ms) && ms > 0);
    const best = times.length ? Math.min(...times) : null;
    let running = 0;
    return {
      mode: 'splits',
      note: 'El parcial es el tiempo de ese tramo. El acumulado es el tiempo desde la salida hasta terminar ese tramo.',
      rows: details.splits_ms.map((split, index) => {
        const splitMs = Number(split);
        const stored = Number(cumulative[index]);
        running += Number.isFinite(splitMs) ? splitMs : 0;
        const acum = Number.isFinite(stored) && stored > 0 ? stored : running;
        return {
          key: `split-${index}`,
          label: splitTramoLabel(mark.event_name, index, details.splits_ms.length),
          split: formatMs(splitMs),
          cumulative: formatMs(acum),
          best: best != null && splitMs === best,
        };
      }),
    };
  }
  if (details.goals != null || details.misses != null) {
    return {
      mode: 'text',
      note: 'Resumen de los tiros registrados.',
      text: `${details.goals || 0} goles · ${details.misses || 0} fallos`,
    };
  }
  const result = formatResult(mark);
  return result && result !== '—'
    ? { mode: 'text', note: 'Esta prueba no tiene parciales ni tiros guardados.', text: `Tiempo total ${result}` }
    : { mode: 'empty' };
};

const formatBestSplit = (mark) => {
  const details = mark.details;
  const splits = details && Array.isArray(details.splits_ms) ? details.splits_ms : [];
  const times = splits.map((item) => Number(item)).filter((ms) => Number.isFinite(ms) && ms > 0);
  if (!times.length) return '—';
  return formatMs(Math.min(...times));
};

const effectivenessPct = (mark) => {
  const details = mark.details;
  if (!details || !Array.isArray(details.attempts) || !details.attempts.length) return null;
  const goals = Number.isInteger(details.goals)
    ? details.goals
    : details.attempts.filter(Boolean).length;
  return Math.round((goals / details.attempts.length) * 100);
};

const bestSplitMs = (mark) => {
  const details = mark.details;
  const splits = details && Array.isArray(details.splits_ms) ? details.splits_ms : [];
  const times = splits.map((item) => Number(item)).filter((ms) => Number.isFinite(ms) && ms > 0);
  return times.length ? Math.min(...times) : null;
};

const goalStatus = (mark, event) => {
  const pct = effectivenessPct(mark);
  if (pct != null) {
    const threshold = Number(event?.effectiveness_threshold);
    const bar = threshold >= 1 ? threshold : 70;
    return pct >= bar ? 'pass' : 'fail';
  }
  const best = bestSplitMs(mark);
  const target = Number(event?.target_split_ms);
  if (best != null && target >= 1) {
    return best <= target ? 'pass' : 'fail';
  }
  return null;
};

const GoalStatus = ({ status }) => {
  if (!status) return <span className="perf-goal is-empty">—</span>;
  const pass = status === 'pass';
  const label = pass ? 'Sí cumplió' : 'No cumplió';
  return (
    <span
      className={`perf-goal ${pass ? 'is-pass' : 'is-fail'}`}
      title={label}
      aria-label={label}
    >
      {label}
    </span>
  );
};

const SortIcon = ({ active, direction }) => (
  <svg className={`perf-sort-icon ${active ? 'is-active' : ''}`} viewBox="0 0 12 14" aria-hidden="true">
    <path
      d="M6 1.2 9.2 5H2.8L6 1.2Z"
      className={active && direction === 'asc' ? 'is-on' : ''}
    />
    <path
      d="M6 12.8 2.8 9h6.4L6 12.8Z"
      className={active && direction === 'desc' ? 'is-on' : ''}
    />
  </svg>
);

const SortHeader = ({ label, sortKey, sort, onSort }) => (
  <th>
    <button type="button" className="perf-sort" onClick={() => onSort(sortKey)}>
      <span>{label}</span>
      <SortIcon active={sort.key === sortKey} direction={sort.direction} />
    </button>
  </th>
);

const studentLabel = (student) =>
  `${student.first_name || ''} ${student.last_name || ''}`.trim() || `Alumno #${student.id}`;

const normalizeSearch = (value) =>
  (value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const studentMatches = (student, query) => {
  const tokens = normalizeSearch(query).trim().split(/\s+/).filter(Boolean);
  if (!tokens.length) return false;
  const haystack = normalizeSearch(
    `${student.first_name || ''} ${student.last_name || ''} ${student.document_number || ''}`
  );
  return tokens.every((token) => haystack.includes(token));
};

const StudentTypeahead = ({ students, selectedId, onChange, placeholder, resetVersion = 0 }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!selectedId) return;
    const current = students.find((item) => String(item.id) === String(selectedId));
    if (current) setQuery(studentLabel(current));
  }, [selectedId, students]);

  useEffect(() => {
    if (resetVersion > 0) setQuery('');
  }, [resetVersion]);

  useEffect(() => {
    const onClick = (event) => {
      if (boxRef.current && !boxRef.current.contains(event.target)) {
        setOpen(false);
        const current = students.find((item) => String(item.id) === String(selectedId));
        setQuery(current ? studentLabel(current) : '');
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [selectedId, students]);

  const results = useMemo(() => {
    if (!query.trim()) return [];
    return students.filter((item) => studentMatches(item, query)).slice(0, 12);
  }, [students, query]);

  return (
    <div className="perf-typeahead" ref={boxRef}>
      <input
        type="text"
        value={query}
        placeholder={placeholder}
        autoComplete="off"
        onFocus={() => {
          setOpen(true);
          if (selectedId) {
            const current = students.find((item) => String(item.id) === String(selectedId));
            if (current) setQuery(studentLabel(current));
          }
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          if (selectedId) onChange('');
        }}
      />
      {selectedId ? (
        <button
          type="button"
          className="perf-typeahead-clear"
          title="Quitar alumno"
          onClick={() => {
            onChange('');
            setQuery('');
            setOpen(false);
          }}
        >
          ×
        </button>
      ) : null}
      {open && query.trim() ? (
        <div className="perf-typeahead-list">
          {results.length ? (
            results.map((student) => (
              <button
                type="button"
                key={student.id}
                className="perf-typeahead-item"
                onClick={() => {
                  onChange(String(student.id));
                  setQuery(studentLabel(student));
                  setOpen(false);
                }}
              >
                <span>{studentLabel(student)}</span>
                {student.document_number ? (
                  <small>{student.document_number}</small>
                ) : null}
              </button>
            ))
          ) : (
            <div className="perf-typeahead-empty">No se encontró ese alumno</div>
          )}
        </div>
      ) : null}
    </div>
  );
};

const PerformanceResults = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canDeleteMarks = canManagePerformance(user);
  const groupRequired = teacherMustChooseGroup(user);
  const [filters, setFilters] = useState(emptyFilters);
  const [filterDraft, setFilterDraft] = useState(emptyFilters);
  const [showRegister, setShowRegister] = useState(false);
  const [selectedMarkIds, setSelectedMarkIds] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [filterResetVersion, setFilterResetVersion] = useState(0);
  const [expandedMarkId, setExpandedMarkId] = useState(null);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ key: 'recorded_at', direction: 'desc' });
  const changeSort = (key) => {
    setSort((current) => (
      current.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: key === 'recorded_at' ? 'desc' : 'asc' }
    ));
    setPage(1);
  };
  const [form, setForm] = useState({
    studentId: '',
    eventId: '',
    groupId: '',
    time: '',
  });
  const [attempts, setAttempts] = useState([]);

  const { data: settings } = useQuery('performance-settings', async () => {
    const response = await api.get('/performance/settings');
    return response.data;
  });

  const { data: studentsData } = useQuery('performance-students', async () => {
    const response = await api.get('/students', { params: { is_active: true, limit: 500 } });
    return asList(response.data);
  });

  const { data: groupsData } = useQuery('performance-groups', async () => {
    const response = await api.get('/groups', { params: { is_active: true } });
    return asList(response.data);
  });

  const events = useMemo(
    () =>
      (settings?.disciplines || []).flatMap((item) =>
        (item.events || [])
          .filter((event) => event.id)
          .map((event) => ({
            ...event,
            discipline_id: item.discipline_id,
            discipline_name: item.discipline_name,
          }))
      ),
    [settings]
  );

  const eventById = useMemo(() => {
    const map = {};
    events.forEach((event) => {
      map[event.id] = event;
    });
    return map;
  }, [events]);

  const students = studentsData || [];
  const groups = groupRequired ? groupsForTeacher(groupsData || [], user?.id) : (groupsData || []);
  const selectedGroup = groups.find((group) => String(group.id) === String(form.groupId));

  const { data: groupEnrollments } = useQuery(
    ['performance-group-enrollments', form.groupId],
    async () => {
      const response = await api.get('/enrollments/', {
        params: { group_id: form.groupId, status: 'active', limit: 500 },
      });
      return asList(response.data);
    },
    { enabled: Boolean(form.groupId) }
  );

  const filterEvents = events.filter((event) =>
    filterDraft.disciplineId
      ? String(event.discipline_id) === String(filterDraft.disciplineId)
      : true
  );
  const selectedEvent = events.find((event) => String(event.id) === String(form.eventId));
  const isScore = selectedEvent?.kind === 'score';
  const formEvents = eventsForGroup(events, selectedGroup).filter((event) => (
    groupRequired ? Boolean(selectedGroup) : true
  ));
  const formGroups = groupsForDiscipline(groups, selectedEvent?.discipline_id);
  const formStudents = groupRequired && !form.groupId
    ? []
    : form.groupId
      ? studentsInGroup(students, groupEnrollments)
      : students;

  const marksParams = {
    limit: 200,
    ...(filters.disciplineId ? { discipline_id: filters.disciplineId } : {}),
    ...(filters.eventId ? { event_id: filters.eventId } : {}),
    ...(filters.studentId ? { student_id: filters.studentId } : {}),
    ...(filters.groupId ? { group_id: filters.groupId } : {}),
    ...(filters.dateFrom ? { date_from: filters.dateFrom } : {}),
    ...(filters.dateTo ? { date_to: filters.dateTo } : {}),
  };

  const {
    data: marks,
    isLoading,
    error,
  } = useQuery(
    ['performance-marks', marksParams],
    async () => {
      const response = await api.get('/performance/marks', { params: marksParams });
      return asList(response.data);
    }
  );

  const resultRows = useMemo(() => {
    const rows = [...(marks || [])];
    const direction = sort.direction === 'asc' ? 1 : -1;
    const text = (value) => (value || '').toString();
    const eventOf = (mark) => eventById[mark.event_id] || eventById[String(mark.event_id)];
    const valueOf = (mark) => {
      if (sort.key === 'recorded_at') return parseApiDateTimeUtc(mark.recorded_at)?.getTime() ?? 0;
      if (sort.key === 'student_name') return text(mark.student_name);
      if (sort.key === 'discipline_name') return text(mark.discipline_name);
      if (sort.key === 'event_name') return text(mark.event_name);
      if (sort.key === 'group_name') return text(mark.group_name);
      if (sort.key === 'result') return mark.time_ms || effectivenessPct(mark) || 0;
      if (sort.key === 'effectiveness') return effectivenessPct(mark) ?? -1;
      if (sort.key === 'best_split') return bestSplitMs(mark) ?? Number.MAX_SAFE_INTEGER;
      if (sort.key === 'goal') {
        const status = goalStatus(mark, eventOf(mark));
        if (status === 'pass') return 2;
        if (status === 'fail') return 1;
        return 0;
      }
      return 0;
    };
    rows.sort((left, right) => {
      const a = valueOf(left);
      const b = valueOf(right);
      if (typeof a === 'string' || typeof b === 'string') {
        return text(a).localeCompare(text(b), 'es', { sensitivity: 'base' }) * direction;
      }
      return (a - b) * direction;
    });
    return rows;
  }, [marks, sort, eventById]);
  const scopedRows = useMemo(() => {
    if (!filters.goal) return resultRows;
    return resultRows.filter((mark) => {
      const event = eventById[mark.event_id] || eventById[String(mark.event_id)];
      const status = goalStatus(mark, event);
      return filters.goal === 'pass' ? status === 'pass' : status === 'fail';
    });
  }, [resultRows, filters.goal, eventById]);
  const effectivenessRows = scopedRows
    .map((mark) => effectivenessPct(mark))
    .filter((value) => value != null);
  const averageEffectiveness = effectivenessRows.length
    ? Math.round(effectivenessRows.reduce((total, value) => total + value, 0) / effectivenessRows.length)
    : null;
  const goalRows = scopedRows.map((mark) => {
    const event = eventById[mark.event_id] || eventById[String(mark.event_id)];
    return goalStatus(mark, event);
  }).filter(Boolean);
  const completedGoals = goalRows.filter((status) => status === 'pass').length;
  const completedGoalsPct = goalRows.length
    ? Math.round((completedGoals / goalRows.length) * 100)
    : null;
  const evaluatedStudents = new Set(scopedRows.map((mark) => mark.student_id)).size;
  const pageSize = 10;
  const pageCount = Math.max(1, Math.ceil(scopedRows.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const visibleMarks = scopedRows.slice((safePage - 1) * pageSize, safePage * pageSize);
  const visibleMarkIds = visibleMarks.map((mark) => mark.id);
  const allVisibleSelected = visibleMarkIds.length > 0 && visibleMarkIds.every((id) => selectedMarkIds.includes(id));

  useEffect(() => {
    const availableIds = new Set(scopedRows.map((mark) => mark.id));
    setSelectedMarkIds((current) => current.filter((id) => availableIds.has(id)));
  }, [scopedRows]);

  const saveMutation = useMutation(
    async (payload) => {
      const response = await api.post('/performance/marks', payload);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('performance-marks');
        queryClient.invalidateQueries('performance-settings');
        toast.success('Resultado registrado');
        setForm((prev) => ({ ...prev, time: '' }));
        setAttempts([]);
        setShowRegister(false);
      },
      onError: (err) => {
        toast.error(getApiErrorMessage(err, 'No se pudo registrar el resultado'));
      },
    }
  );

  const bulkDeleteMutation = useMutation(
    async (markIds) => {
      await Promise.all(markIds.map((markId) => api.delete(`/performance/marks/${markId}`)));
      return markIds.length;
    },
    {
      onSuccess: (count) => {
        queryClient.invalidateQueries('performance-marks');
        queryClient.invalidateQueries('performance-settings');
        setSelectedMarkIds([]);
        toast.success(`${count} resultado${count === 1 ? '' : 's'} eliminado${count === 1 ? '' : 's'}`);
      },
      onError: (err) => {
        queryClient.invalidateQueries('performance-marks');
        toast.error(err.response?.data?.detail || 'No se pudieron eliminar todos los resultados');
      },
    }
  );

  const applyFilters = (event) => {
    event.preventDefault();
    if (filterDraft.dateFrom && filterDraft.dateTo && filterDraft.dateFrom > filterDraft.dateTo) {
      toast.error('La fecha inicial no puede ser posterior a la fecha final');
      return;
    }
    setFilters({ ...filterDraft });
    setPage(1);
    setSelectedMarkIds([]);
  };

  const clearFilters = () => {
    const cleared = emptyFilters();
    setFilterDraft(cleared);
    setFilters(cleared);
    setPage(1);
    setSelectedMarkIds([]);
    setFilterResetVersion((current) => current + 1);
  };

  const toggleVisibleSelection = () => {
    setSelectedMarkIds((current) => {
      if (allVisibleSelected) {
        return current.filter((id) => !visibleMarkIds.includes(id));
      }
      return Array.from(new Set([...current, ...visibleMarkIds]));
    });
  };

  const toggleMarkSelection = (markId) => {
    setSelectedMarkIds((current) =>
      current.includes(markId)
        ? current.filter((id) => id !== markId)
        : [...current, markId]
    );
  };

  const removeSelectedMarks = () => {
    if (!selectedMarkIds.length) return;
    const count = selectedMarkIds.length;
    const ids = [...selectedMarkIds];
    setConfirmDialog({
      title: `¿Eliminar ${count} resultado${count === 1 ? '' : 's'}?`,
      message: 'Dejarán de verse en la web y en la app.',
      type: 'danger',
      confirmText: 'Eliminar',
      onConfirm: () => bulkDeleteMutation.mutate(ids),
    });
  };

  const submit = (e) => {
    e.preventDefault();
    if (groupRequired && !form.groupId) {
      toast.error('Elige el grupo');
      return;
    }
    if (!form.studentId || !form.eventId) {
      toast.error('Elige alumno y prueba');
      return;
    }
    if (
      (form.groupId && !formGroups.some((group) => String(group.id) === String(form.groupId)))
      || !formEvents.some((event) => String(event.id) === String(form.eventId))
      || !formStudents.some((student) => String(student.id) === String(form.studentId))
    ) {
      toast.error('El alumno, el grupo y la prueba no corresponden');
      return;
    }
    if (isScore) {
      if (attempts.length < 1) {
        toast.error('Registra al menos un tiro');
        return;
      }
      saveMutation.mutate(buildMarkPayload({
        studentId: form.studentId,
        eventId: form.eventId,
        groupId: form.groupId,
        attempts,
        isScore: true,
      }));
      return;
    }
    const timeMs = parseTimeToMs(form.time);
    if (!timeMs) {
      toast.error('Escribe el tiempo como 01:38.20');
      return;
    }
    saveMutation.mutate(buildMarkPayload({
      studentId: form.studentId,
      eventId: form.eventId,
      groupId: form.groupId,
      timeMs,
      isScore: false,
    }));
  };

  const goals = attempts.filter(Boolean).length;

  return (
    <div className="page-container perf-results">
      <ConfirmDialog
        isOpen={Boolean(confirmDialog)}
        onClose={() => setConfirmDialog(null)}
        onConfirm={confirmDialog?.onConfirm || (() => {})}
        title={confirmDialog?.title}
        message={confirmDialog?.message}
        type={confirmDialog?.type || 'warning'}
        confirmText={confirmDialog?.confirmText}
      />
      <div className="page-header perf-results-header">
        <div>
          <h2>Historial de evaluaciones</h2>
          <p>Consulta, filtra y revisa el rendimiento de tus alumnos.</p>
        </div>
        <button
          type="button"
          className="btn btn-primary perf-register-toggle"
          onClick={() => setShowRegister((current) => !current)}
          aria-expanded={showRegister}
        >
          {showRegister ? 'Cerrar registro' : '+ Registrar evaluación'}
        </button>
      </div>

      {!settings?.enabled && (
        <div className="card perf-results-warn">
          El módulo de pruebas está apagado. Actívalo en Configurar para que también aparezca en la app.
        </div>
      )}

      <div className="perf-summary-grid">
        <div className="card perf-summary-card">
          <span className="perf-summary-icon is-red" aria-hidden="true">E</span>
          <div><span>Evaluaciones</span><strong>{scopedRows.length}</strong></div>
        </div>
        <div className="card perf-summary-card">
          <span className="perf-summary-icon is-green" aria-hidden="true">%</span>
          <div><span>Efectividad promedio</span><strong>{averageEffectiveness == null ? '—' : `${averageEffectiveness}%`}</strong></div>
        </div>
        <div className="card perf-summary-card">
          <span className="perf-summary-icon is-emerald" aria-hidden="true">✓</span>
          <div><span>Objetivos cumplidos</span><strong>{completedGoalsPct == null ? '—' : `${completedGoalsPct}%`}</strong></div>
        </div>
        <div className="card perf-summary-card">
          <span className="perf-summary-icon is-blue" aria-hidden="true">A</span>
          <div><span>Alumnos evaluados</span><strong>{evaluatedStudents}</strong></div>
        </div>
      </div>

      {showRegister && <form className="card perf-register" onSubmit={submit}>
        <h3>Registrar resultado</h3>
        <div className="perf-register-grid">
          <label>
            Alumno
            <StudentTypeahead
              students={formStudents}
              selectedId={form.studentId}
              placeholder="Escribe el nombre o DNI"
              onChange={(studentId) => setForm((prev) => ({ ...prev, studentId }))}
            />
          </label>
          <label>
            Prueba
            <select
              value={form.eventId}
              onChange={(e) => {
                const eventId = e.target.value;
                const event = events.find((item) => String(item.id) === String(eventId));
                setForm((prev) => {
                  const group = groups.find((item) => String(item.id) === String(prev.groupId));
                  const groupFits = !event || !group || String(group.discipline_id) === String(event.discipline_id);
                  return {
                    ...prev,
                    eventId,
                    time: '',
                    groupId: groupFits ? prev.groupId : '',
                    studentId: groupFits ? prev.studentId : '',
                  };
                });
                setAttempts([]);
              }}
            >
              <option value="">Selecciona</option>
              {formEvents.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.discipline_name} — {event.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            {groupRequired ? 'Grupo' : 'Grupo (opcional)'}
            <select
              value={form.groupId}
              onChange={(e) => {
                const group = formGroups.find((item) => String(item.id) === String(e.target.value));
                setForm((prev) => formAfterGroupChange(prev, group, events));
                setAttempts([]);
              }}
            >
              <option value="">{groupRequired ? 'Selecciona' : 'Sin grupo'}</option>
              {formGroups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </label>
          {isScore ? (
            <div className="perf-score-box">
              <span>
                {goals} goles / {attempts.length - goals} fallos
                {attempts.length ? ` · ${Math.round((goals / attempts.length) * 100)}%` : ''}
              </span>
              <div className="perf-score-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setAttempts((prev) => [...prev, true])}
                  disabled={
                    selectedEvent?.attempt_count
                      ? attempts.length >= selectedEvent.attempt_count
                      : false
                  }
                >
                  Gol
                </button>
                <button
                  type="button"
                  className="btn btn-secondary perf-miss"
                  onClick={() => setAttempts((prev) => [...prev, false])}
                  disabled={
                    selectedEvent?.attempt_count
                      ? attempts.length >= selectedEvent.attempt_count
                      : false
                  }
                >
                  Fallo
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setAttempts([])}>
                  Limpiar
                </button>
              </div>
            </div>
          ) : (
            <label>
              Tiempo
              <input
                type="text"
                placeholder="01:38.20"
                value={form.time}
                onChange={(e) => setForm((prev) => ({ ...prev, time: e.target.value }))}
              />
            </label>
          )}
        </div>
        <button type="submit" className="btn btn-primary" disabled={saveMutation.isLoading}>
          {saveMutation.isLoading ? 'Guardando...' : 'Guardar resultado'}
        </button>
      </form>}

      <div className="card perf-history-card">
        <form className="perf-filters" onSubmit={applyFilters}>
          <label>
            Disciplina
            <select
              value={filterDraft.disciplineId}
              onChange={(e) =>
                setFilterDraft((prev) => ({ ...prev, disciplineId: e.target.value, eventId: '' }))
              }
            >
              <option value="">Todas</option>
              {(settings?.disciplines || []).map((item) => (
                <option key={item.discipline_id} value={item.discipline_id}>
                  {item.discipline_name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Prueba
            <select
              value={filterDraft.eventId}
              onChange={(e) => setFilterDraft((prev) => ({ ...prev, eventId: e.target.value }))}
            >
              <option value="">Todas</option>
              {filterEvents.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Alumno
            <StudentTypeahead
              students={students}
              selectedId={filterDraft.studentId}
              placeholder="Buscar alumno o dejar vacío"
              resetVersion={filterResetVersion}
              onChange={(studentId) => setFilterDraft((prev) => ({ ...prev, studentId }))}
            />
          </label>
          <label>
            Grupo
            <select
              value={filterDraft.groupId}
              onChange={(e) => setFilterDraft((prev) => ({ ...prev, groupId: e.target.value }))}
            >
              <option value="">Todos</option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Desde
            <input
              type="date"
              value={filterDraft.dateFrom}
              max={filterDraft.dateTo || undefined}
              onChange={(e) => setFilterDraft((prev) => ({ ...prev, dateFrom: e.target.value }))}
            />
          </label>
          <label>
            Hasta
            <input
              type="date"
              value={filterDraft.dateTo}
              min={filterDraft.dateFrom || undefined}
              onChange={(e) => setFilterDraft((prev) => ({ ...prev, dateTo: e.target.value }))}
            />
          </label>
          <label>
            Objetivo
            <select
              value={filterDraft.goal}
              onChange={(e) => setFilterDraft((prev) => ({ ...prev, goal: e.target.value }))}
            >
              <option value="">Todos</option>
              <option value="pass">Cumplió</option>
              <option value="fail">No cumplió</option>
            </select>
          </label>
          <div className="perf-filter-actions">
            <button type="submit" className="btn btn-primary">Filtrar</button>
            <button type="button" className="btn btn-secondary" onClick={clearFilters}>Limpiar</button>
          </div>
        </form>

        {canDeleteMarks ? <div className="perf-bulk-bar">
          <div className="perf-bulk-left">
            <label className="perf-select-page">
              <input
                type="checkbox"
                checked={allVisibleSelected}
                onChange={toggleVisibleSelection}
                disabled={!visibleMarks.length}
              />
              Seleccionar página
            </label>
            <button
              type="button"
              className="perf-bulk-delete"
              onClick={removeSelectedMarks}
              disabled={!selectedMarkIds.length || bulkDeleteMutation.isLoading}
            >
              <span className="perf-bulk-icon" aria-hidden="true">
                <svg viewBox="0 0 16 16">
                  <path d="M6.2 2.2h3.6l.4.8H13v1.2H3V3h2.8l.4-.8ZM4.2 5.2h7.6l-.5 8.1a1 1 0 0 1-1 .9H5.7a1 1 0 0 1-1-.9l-.5-8.1Z" />
                </svg>
              </span>
              {bulkDeleteMutation.isLoading ? 'Eliminando...' : 'Eliminar'}
            </button>
          </div>
          <button
            type="button"
            className="perf-clear-selection"
            onClick={() => setSelectedMarkIds([])}
            disabled={!selectedMarkIds.length}
          >
            Eliminar selección
          </button>
        </div> : null}

        {isLoading ? (
          <div className="loading">Cargando resultados...</div>
        ) : error ? (
          <div className="error">No se pudieron cargar los resultados.</div>
        ) : !marks?.length ? (
          <div className="perf-empty">Aún no hay resultados con estos filtros.</div>
        ) : (
          <>
          <div className="perf-table-scroll">
            <table className="perf-table">
              <thead>
                <tr>
                  {canDeleteMarks ? (
                  <th className="perf-col-select">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleVisibleSelection}
                      aria-label="Seleccionar los resultados de esta página"
                    />
                  </th>
                  ) : null}
                  <SortHeader label="Fecha" sortKey="recorded_at" sort={sort} onSort={changeSort} />
                  <SortHeader label="Alumno" sortKey="student_name" sort={sort} onSort={changeSort} />
                  <SortHeader label="Disciplina" sortKey="discipline_name" sort={sort} onSort={changeSort} />
                  <SortHeader label="Prueba" sortKey="event_name" sort={sort} onSort={changeSort} />
                  <SortHeader label="Resultado" sortKey="result" sort={sort} onSort={changeSort} />
                  <SortHeader label="Efectividad" sortKey="effectiveness" sort={sort} onSort={changeSort} />
                  <SortHeader label="Mejor parcial" sortKey="best_split" sort={sort} onSort={changeSort} />
                  <SortHeader label="Objetivo" sortKey="goal" sort={sort} onSort={changeSort} />
                  <SortHeader label="Grupo" sortKey="group_name" sort={sort} onSort={changeSort} />
                  <th className="perf-col-actions">Detalle</th>
                </tr>
              </thead>
              <tbody>
                {visibleMarks.map((mark) => {
                  const event = eventById[mark.event_id] || eventById[String(mark.event_id)];
                  const status = goalStatus(mark, event);
                  const expanded = expandedMarkId === mark.id;
                  const when = formatDateParts(mark.recorded_at);
                  return (
                  <React.Fragment key={mark.id}>
                  <tr className={selectedMarkIds.includes(mark.id) ? 'is-selected' : ''}>
                    {canDeleteMarks ? (
                    <td className="perf-col-select">
                      <input
                        type="checkbox"
                        checked={selectedMarkIds.includes(mark.id)}
                        onChange={() => toggleMarkSelection(mark.id)}
                        aria-label={`Seleccionar resultado de ${mark.student_name}`}
                      />
                    </td>
                    ) : null}
                    <td className="perf-date">
                      <span>{when.date}</span>
                      {when.time ? <small>{when.time}</small> : null}
                    </td>
                    <td className="perf-student">{mark.student_name}</td>
                    <td>{mark.discipline_name || '—'}</td>
                    <td>{mark.event_name}</td>
                    <td>{formatResult(mark)}</td>
                    <td>{formatEffectiveness(mark)}</td>
                    <td>{formatBestSplit(mark)}</td>
                    <td>
                      <GoalStatus status={status} />
                    </td>
                    <td>{mark.group_name || '—'}</td>
                    <td className="perf-col-actions">
                      <button
                        type="button"
                        className={`perf-expand ${expanded ? 'is-open' : ''}`}
                        aria-expanded={expanded}
                        aria-label={expanded ? 'Ocultar detalle' : 'Ver detalle'}
                        title={expanded ? 'Ocultar detalle' : 'Ver detalle'}
                        onClick={() => setExpandedMarkId(expanded ? null : mark.id)}
                      >
                        <svg viewBox="0 0 16 16" aria-hidden="true">
                          <path d={expanded ? 'M3.5 10.2 8 5.8l4.5 4.4' : 'M3.5 5.8 8 10.2l4.5-4.4'} />
                        </svg>
                      </button>
                    </td>
                  </tr>
                  {expanded && (
                    <tr className="perf-detail-row">
                      <td colSpan="11">
                        {(() => {
                          const detail = markDetail(mark);
                          if (detail.mode === 'empty') {
                            return <span>Sin detalle adicional.</span>;
                          }
                          return (
                            <>
                              <div className="perf-detail-title">Detalle de la prueba</div>
                              {detail.note ? <p className="perf-detail-note">{detail.note}</p> : null}
                              {detail.mode === 'splits' ? (
                                <table className="perf-split-table">
                                  <thead>
                                    <tr>
                                      <th>Tramo</th>
                                      <th>Parcial</th>
                                      <th>Acumulado</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {detail.rows.map((row) => (
                                      <tr key={row.key} className={row.best ? 'is-best' : ''}>
                                        <td>{row.label}</td>
                                        <td>
                                          {row.split}
                                          {row.best ? <span className="perf-best-tag">Mejor</span> : null}
                                        </td>
                                        <td>{row.cumulative}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              ) : detail.mode === 'attempts' ? (
                                <div className="perf-detail-list">
                                  {detail.rows.map((line) => (
                                    <span key={line.key} className={line.kind === 'goal' ? 'is-goal' : 'is-miss'}>
                                      {line.text}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span>{detail.text}</span>
                              )}
                            </>
                          );
                        })()}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {pageCount > 1 && (
              <div className="perf-pagination">
                <button type="button" disabled={safePage === 1} onClick={() => setPage(safePage - 1)}>
                  Anterior
                </button>
                <span>Página {safePage} de {pageCount}</span>
                <button
                  type="button"
                  disabled={safePage === pageCount}
                  onClick={() => setPage(safePage + 1)}
                >
                  Siguiente
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PerformanceResults;
